import React, { useState, useEffect } from 'react';
import { CloseIcon, SearchIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { usePublicationHighlights, useUpdatePublicationHighlights, usePublications } from '@/lib/api/hooks/publication';
import { showToast } from '@/lib/toast';
import { Publication } from '@/lib/api/types';
import { WebsitePageSection } from '@/types';
import { X } from 'lucide-react';

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
};

interface InlinePublicationHighlightEditorProps {
    data: WebsitePageSection;
    onSave: (data: Partial<WebsitePageSection>) => void;
    onCancel: () => void;
}

const InlinePublicationHighlightEditor: React.FC<InlinePublicationHighlightEditorProps> = ({
    onCancel,
}) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedPublications, setSelectedPublications] = useState<Publication[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const { data: highlightsData } = usePublicationHighlights('HOME_PAGE_KEY_RESOURCES');

    const { data: publicationsData } = usePublications(
        { page: 1, size: 50 },
        { status: 'PUBLISH' }
    );

    const updateHighlightsMutation = useUpdatePublicationHighlights();

    const allPublications = publicationsData?.data?.data || [];

    // Filter out already selected and apply search
    const availablePublications = allPublications.filter((pub: Publication) =>
        !selectedIds.includes(pub.publicationId) &&
        (pub.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            pub.description?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    useEffect(() => {
        if (highlightsData?.data) {
            const currentIds = highlightsData.data.map((pub: Publication) => pub.publicationId);
            setSelectedIds(currentIds);
            setSelectedPublications(highlightsData.data);
        }
    }, [highlightsData]);

    const handleToggleSelect = (publicationId: string) => {
        const isAlreadySelected = selectedIds.includes(publicationId);

        if (isAlreadySelected) {
            setSelectedIds(prev => prev.filter(id => id !== publicationId));
            setSelectedPublications(prev => prev.filter(p => p.publicationId !== publicationId));
        } else {
            if (selectedIds.length >= 6) {
                showToast.error("You can only select up to 6 publications.");
                return;
            }
            const pubToAdd = allPublications.find((p: Publication) => p.publicationId === publicationId);
            if (pubToAdd) {
                setSelectedIds(prev => [...prev, publicationId]);
                setSelectedPublications(prev => [...prev, pubToAdd]);
            }
        }
    };

    const handleSave = async () => {
        try {
            await updateHighlightsMutation.mutateAsync({
                publicationIds: selectedIds,
                type: 'HOME_PAGE_KEY_RESOURCES'
            });
            showToast.success("Key resources updated successfully!");
            onCancel();
        } catch (error) {
            showToast.error("Failed to update key resources. Please try again.");
        }
    };

    const formatCategory = (category: string) => {
        return category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    };

    const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-2_xbv8lj.png";

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-lg text-gray-900">Select Key Resources</h3>
                <button
                    onClick={onCancel}
                    className="text-gray-500 hover:text-gray-700 p-1"
                >
                    <CloseIcon className="w-5 h-5" />
                </button>
            </div>

            {selectedPublications.length > 0 && (
                <div className="p-4 border-b border-gray-200 bg-wine-50/30">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Currently Selected ({selectedPublications.length}/6)</h4>
                    </div>
                    <div className="space-y-2">
                        {selectedPublications.map((publication) => (
                            <div
                                key={`selected-${publication.publicationId}`}
                                className="flex items-start gap-3 bg-white border border-[#681628] rounded-lg p-3 group"
                            >
                                <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                    <img
                                        src={publication.coverImageUrl || defaultImage}
                                        alt={publication.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            if (e.currentTarget.src !== defaultImage) {
                                                e.currentTarget.src = defaultImage;
                                            }
                                        }}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className="text-sm font-medium text-gray-900 truncate">{publication.title}</h5>
                                    {(publication.datePublished || publication.createdByName) ? (
                                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                            {publication.datePublished && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(publication.datePublished)}</p>
                                            )}
                                            {publication.createdByName && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {publication.createdByName}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                                            {formatCategory(publication.publicationCategory)}
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleToggleSelect(publication.publicationId)}
                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                                    title="Remove from selection"
                                >
                                    <X className="w-4 h-4 text-gray-500 group-hover:text-[#681628]" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="p-4 border-b border-gray-200">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search publications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-10 pl-4 pr-12 bg-gray-50 text-base font-normal text-gray-900 placeholder-gray-600 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#951F39] focus:border-transparent"
                    />
                    {searchTerm ? (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center hover:bg-[#801b31] transition-colors"
                        >
                            <CloseIcon className="w-5 h-5 text-white" />
                        </button>
                    ) : (
                        <div className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center">
                            <SearchIcon className="w-5 h-5 text-white" />
                        </div>
                    )}
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
                {availablePublications.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        {searchTerm ? 'No publications found matching your search.' : 'No more publications available.'}
                    </div>
                ) : (
                    availablePublications.map((publication: Publication) => (
                        <div
                            key={publication.publicationId}
                            onClick={() => handleToggleSelect(publication.publicationId)}
                            className="flex items-start gap-4 p-3 rounded-lg border cursor-pointer transition-all border-gray-200 hover:border-gray-300"
                        >
                            <div className="relative w-20 h-20 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                <img
                                    src={publication.coverImageUrl || defaultImage}
                                    alt={publication.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        if (e.currentTarget.src !== defaultImage) {
                                            e.currentTarget.src = defaultImage;
                                        }
                                    }}
                                />
                            </div>

                            <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 text-sm truncate">{publication.title}</h4>
                                {(publication.datePublished || publication.createdByName) && (
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                        {publication.datePublished && (
                                            <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(publication.datePublished)}</p>
                                        )}
                                        {publication.createdByName && (
                                            <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {publication.createdByName}</p>
                                        )}
                                    </div>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                        {formatCategory(publication.publicationCategory)}
                                    </span>
                                </div>
                            </div>

                            <div className="flex-shrink-0 pt-1">
                                <div className="w-6 h-6 rounded-full border border-gray-300" />
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
                <Button variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={updateHighlightsMutation.isPending}
                >
                    {updateHighlightsMutation.isPending ? 'Saving...' : 'Save Selection'}
                </Button>
            </div>
        </div>
    );
};

export default InlinePublicationHighlightEditor;
